import { createContext, useContext, useState, type ReactNode } from 'react'

interface SidebarContextType {
  // Mobile sidebar state
  isOpen: boolean
  toggle: () => void
  open: () => void
  close: () => void
  // Desktop collapse state
  isCollapsed: boolean
  toggleCollapse: () => void
  expand: () => void
  collapse: () => void
}

const SidebarContext = createContext<SidebarContextType | null>(null)

export function SidebarProvider({ children }: { children: ReactNode }) {
  // Mobile sidebar state
  const [isOpen, setIsOpen] = useState(false)
  // Desktop collapse state (always starts expanded)
  const [isCollapsed, setIsCollapsed] = useState(false)

  const toggle = () => setIsOpen((prev) => !prev)
  const open = () => setIsOpen(true)
  const close = () => setIsOpen(false)

  const toggleCollapse = () => setIsCollapsed((prev) => !prev)
  const expand = () => setIsCollapsed(false)
  const collapse = () => setIsCollapsed(true)

  return (
    <SidebarContext.Provider
      value={{
        isOpen,
        toggle,
        open,
        close,
        isCollapsed,
        toggleCollapse,
        expand,
        collapse,
      }}
    >
      {children}
    </SidebarContext.Provider>
  )
}

export function useSidebar() {
  const context = useContext(SidebarContext)
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider')
  }
  return context
}
